@file:OptIn(kotlin.concurrent.atomics.ExperimentalAtomicApi::class)

package com.dotpx.ambient

import bio.aq.audio.AudioPlayer
import bio.aq.audio.PcmSource
import kotlin.concurrent.atomics.AtomicBoolean

internal interface ControllerAudioOutput {
    fun start()
    fun pause()
    fun stop()
    fun setContinuousEngineEnabled(enabled: Boolean) = Unit
    fun consumeFailure(): Throwable?
    fun release()
}

internal class NativeControllerAudioOutput(
    render: (buffer: FloatArray, frameCount: Int) -> Unit,
    private val policy: ControllerAudioPolicy = platformControllerAudioPolicy(),
    private val playerFactory: (PcmSource) -> ControllerAudioPlayer = ::LibraryControllerAudioPlayer,
) : ControllerAudioOutput {
    private val source = ControllerPcmSource(render, policy)
    private var player: ControllerAudioPlayer? = null
    private var continuousEngineEnabled = false
    private var pendingFailure: Throwable? = null
    private var released = false

    override fun start() {
        check(!released) { "Ambient audio output has been released" }
        source.resume(policy.fadeInOnStart || continuousEngineEnabled)
        try {
            val output = player ?: playerFactory(source).also { player = it }
            output.play()
        } catch (error: Throwable) {
            source.suspend()
            runCatching { closePlayer() }
            throw error
        }
    }

    override fun pause() {
        source.suspend()
        when {
            policy.closeOnPause -> closePlayer()
            !continuousEngineEnabled -> player?.pause()
        }
    }

    override fun stop() {
        source.suspend()
        closePlayer()
    }

    override fun setContinuousEngineEnabled(enabled: Boolean) {
        continuousEngineEnabled = enabled && policy.supportsContinuousEngine
    }

    override fun consumeFailure(): Throwable? {
        pendingFailure?.let {
            pendingFailure = null
            return it
        }
        return player?.takeFailure()
    }

    override fun release() {
        if (released) return
        released = true
        stop()
    }

    private fun closePlayer() {
        val output = player ?: return
        player = null
        try {
            output.close()
        } finally {
            output.takeFailure()?.let { if (pendingFailure == null) pendingFailure = it }
        }
    }
}

/** Keeps playback lifecycle testable without opening an audio device. */
internal interface ControllerAudioPlayer {
    fun play()
    fun pause()
    fun close()
    fun takeFailure(): Throwable?
}

private class LibraryControllerAudioPlayer(source: PcmSource) : ControllerAudioPlayer {
    private val player = AudioPlayer(source)

    override fun play() = player.play()
    override fun pause() = player.pause()
    override fun close() = player.close()
    override fun takeFailure(): Throwable? = player.takeFailure()
}

/** A silent running engine never advances synthesis or app playback timers. */
private class ControllerPcmSource(
    private val callback: (FloatArray, Int) -> Unit,
    private val policy: ControllerAudioPolicy,
) : PcmSource {
    private val muted = AtomicBoolean(true)
    private val fadeInRequested = AtomicBoolean(false)
    private val renderAudibleCallback: (FloatArray, Int) -> Unit = ::renderAudible
    private var fadeInFrame = AmbientSampleRate

    fun resume(fadeIn: Boolean) {
        if (fadeIn) fadeInRequested.store(true)
        muted.store(false)
    }

    fun suspend() = muted.store(true)

    override fun render(buffer: FloatArray, frameCount: Int) {
        require(frameCount >= 0 && frameCount <= buffer.size / AmbientChannelCount) {
            "The buffer must hold two samples for every requested frame"
        }
        if (muted.load()) {
            buffer.fill(0f, 0, frameCount * AmbientChannelCount)
        } else {
            policy.render(renderAudibleCallback, buffer, frameCount)
        }
    }

    private fun renderAudible(buffer: FloatArray, frameCount: Int) {
        callback(buffer, frameCount)
        if (fadeInRequested.exchange(false)) fadeInFrame = 0
        for (frame in 0 until frameCount) {
            val fadeGain = if (fadeInFrame < AmbientSampleRate) {
                ++fadeInFrame / AmbientSampleRate.toFloat()
            } else {
                1f
            }
            val gain = policy.outputGain * fadeGain
            val offset = frame * AmbientChannelCount
            buffer[offset] *= gain
            buffer[offset + 1] *= gain
        }
    }
}
